import { GOAL_RECOMMENDATION_COUNT_HIDDEN_UNTIL_DURATION } from '@consts';
import * as Sentry from '@sentry/nextjs';
import { useCallback, useMemo } from 'react';
import { useAccountsService } from './useAccountsService';
import { useImprvApi } from './useImprvApi';
import { LocalStorageService } from './useLocalStorage';

export type GoalRecommendationsCaller = 'banner' | 'count';

export const useGoalRecommendationsService = () => {
  const imprvApi = useImprvApi();
  const { accountMe } = useAccountsService();
  const localStorageService = LocalStorageService.getInstance();

  const userId = accountMe?.user?.id ?? null;

  /**
   * Returns recommendations count. If the caller's UI is currently "hidden" via localStorage
   * (banner dismissed or badge hidden after Explore click), skips the API request and returns 0.
   */
  const getGoalsRecommendationsCount = useCallback(
    async (caller: GoalRecommendationsCaller): Promise<{ count: number }> => {
      if (userId == null) {
        return { count: 0 };
      }

      const hiddenUntil = localStorageService.getGoalRecommendationCountHidden(userId, caller);
      if (hiddenUntil) {
        try {
          if (new Date(hiddenUntil).getTime() > Date.now()) {
            return { count: 0 };
          }
        } catch {
          // invalid date, fall through to fetch
        }
      }

      try {
        return await imprvApi.getGoalsRecommendationsCount();
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi, userId, localStorageService]
  );

  /**
   * Marks recommendations as seen for the given caller (banner or count badge).
   * Sets localStorage so the banner/badge is hidden for one week.
   */
  const markRecommendationsSeen = useCallback(
    (caller: GoalRecommendationsCaller): void => {
      if (userId == null) return;
      const until = new Date(
        Date.now() + GOAL_RECOMMENDATION_COUNT_HIDDEN_UNTIL_DURATION
      ).toISOString();
      localStorageService.setGoalRecommendationCountHidden(userId, caller, until);
    },
    [userId, localStorageService]
  );

  return useMemo(
    () => ({
      getGoalsRecommendationsCount,
      markRecommendationsSeen
    }),
    [getGoalsRecommendationsCount, markRecommendationsSeen]
  );
};
